import Papa from "papaparse";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { truncateStringValue } from "../../lib/truncate-string-value";
import "./CsvImporter.scss";

interface CsvImporterProps {
  file: File;
  previewRowLimit?: number;
  headers?: string[];
  selectedHeaders?: Record<number, boolean>;
  onDataUpdate?: (
    data: string[][],
    headers: string[],
    selectedHeaders: Record<number, boolean>,
  ) => void;
}

const CsvImporter: React.FC<CsvImporterProps> = ({
  file,
  previewRowLimit = 10,
  headers: externalHeaders,
  selectedHeaders: externalSelectedHeaders,
  onDataUpdate,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [data, setData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>(externalHeaders ?? []);
  const [selectedHeaders, setSelectedHeaders] = useState<
    Record<number, boolean>
  >(externalSelectedHeaders ?? {});

  const handleDataUpdate = useCallback(() => {
    const selectedHeaderIndices = Object.keys(selectedHeaders).map(Number);
    const headerRow = selectedHeaderIndices.map((index) => headers[index]);
    const selectedData = data.map((row) =>
      selectedHeaderIndices.map((index) => row[index]),
    );
    onDataUpdate?.([headerRow, ...selectedData], headers, selectedHeaders);
  }, [data, headers, onDataUpdate, selectedHeaders]);

  useEffect(() => {
    handleDataUpdate();
  }, [data, headers, selectedHeaders, handleDataUpdate]);

  useEffect(() => {
    if (file && !isInitialized) {
      setIsInitialized(true);
      Papa.parse(file, {
        complete: (results) => {
          const csvData = results.data as string[][];
          if (!externalHeaders) {
            setHeaders(csvData[0]);
          }

          setData(csvData.slice(1));
          if (!externalSelectedHeaders) {
            setSelectedHeaders(
              csvData[0].reduce(
                (acc, _header, index) => {
                  acc[index] = true;
                  return acc;
                },
                {} as Record<number, boolean>,
              ),
            );
          }
        },
      });
    }
  }, [
    file,
    onDataUpdate,
    handleDataUpdate,
    isInitialized,
    externalHeaders,
    externalSelectedHeaders,
  ]);

  const handleHeaderChange = useCallback(
    (index: number, value: string) => {
      const newHeaders = [...headers];
      newHeaders[index] = value;
      setHeaders(newHeaders);
      handleDataUpdate();
    },
    [headers, handleDataUpdate],
  );

  const handleHeaderSelect = useCallback(
    (header: string) => {
      const headerIndex = headers.indexOf(header);
      const newSelectedHeaders = {
        ...selectedHeaders,
        [headerIndex]: !selectedHeaders[headerIndex],
      };
      setSelectedHeaders(newSelectedHeaders);
      handleDataUpdate();
    },
    [headers, selectedHeaders, handleDataUpdate],
  );

  const dataToDisplay = useMemo(
    () => data.slice(0, previewRowLimit),
    [data, previewRowLimit],
  );

  return (
    <div className="csv-importer">
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              {headers.map((header, i) => (
                <th key={i}>
                  <div className="selectable-header">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedHeaders[i] ?? false}
                      onChange={() => handleHeaderSelect(header)}
                    />
                    <input
                      type="text"
                      className="form-control"
                      value={header}
                      onChange={(e) => handleHeaderChange(i, e.target.value)}
                    />
                  </div>
                  <div className="header-text-container">{header || "--"}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataToDisplay.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{truncateStringValue(cell, 32)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default memo(CsvImporter);
